/**
 * Prompts — the wisdom_prompts editor.
 *
 * The service's GET /prompts filters on category_id and is_active only, so the
 * text search and the "popular only" toggle are applied client-side over the
 * fetched page (limit is raised to 500, the service's cap, so the filters see
 * everything for a category). Create / edit / duplicate all go through the same
 * dialog; DELETE hard-deletes an unused prompt and silently deactivates one
 * that member entries reference — the service tells us which happened and the
 * snackbar repeats it back.
 *
 * CSV bulk import posts the raw file text to /prompts/bulk and renders the
 * per-line error report the service returns.
 */

import React from 'react';
import {
  Box,
  Button,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import QuizIcon from '@mui/icons-material/Quiz';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import EmptyState from '../../common/EmptyState';
import { brandColors } from '../../../theme/brandColors';
import {
  lifeAdviceApi,
  WisdomCategory,
  WisdomPrompt,
  PromptInput,
  BulkImportResult,
} from '../../../services/lifeAdviceAdminService';
import {
  PageHeader,
  errorMessage,
  writeErrorMessage,
  isBackendDown,
} from '../common/lifeAdviceUi';
import LifeAdviceActionConfirm from '../common/LifeAdviceActionConfirm';

const PAGE_LIMIT = 500; // the service's max for /prompts

interface DraftState {
  category_id: string;
  title: string;
  prompt_text: string;
  description: string;
  is_popular: boolean;
  is_active: boolean;
  sort_order: number;
}

const BLANK_DRAFT: DraftState = {
  category_id: '',
  title: '',
  prompt_text: '',
  description: '',
  is_popular: false,
  is_active: true,
  sort_order: 0,
};

const CSV_TEMPLATE =
  'category_slug,title,prompt_text,description,is_popular,sort_order\n' +
  'love,What did you learn about love?,What did you learn about love?,Speak to one person in particular.,true,1';

const PromptsPage: React.FC = () => {
  const [categories, setCategories] = React.useState<WisdomCategory[]>([]);
  const [rows, setRows] = React.useState<WisdomPrompt[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [down, setDown] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);

  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [popularOnly, setPopularOnly] = React.useState(false);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<DraftState>(BLANK_DRAFT);
  const [saving, setSaving] = React.useState(false);
  const [dialogError, setDialogError] = React.useState<string | null>(null);

  const [confirming, setConfirming] = React.useState<WisdomPrompt | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);

  const [importOpen, setImportOpen] = React.useState(false);
  const [importCsv, setImportCsv] = React.useState('');
  const [importFileName, setImportFileName] = React.useState('');
  const [importBusy, setImportBusy] = React.useState(false);
  const [importError, setImportError] = React.useState<string | null>(null);
  const [importResult, setImportResult] = React.useState<BulkImportResult | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setDown(false);
    try {
      const [cats, page] = await Promise.all([
        lifeAdviceApi.categories.list(),
        lifeAdviceApi.prompts.list({
          ...(categoryFilter ? { category_id: categoryFilter } : {}),
          limit: PAGE_LIMIT,
          page: 1,
        }),
      ]);
      setCategories(cats);
      setRows(page.rows);
    } catch (e) {
      setDown(isBackendDown(e));
      setError(errorMessage(e, 'Failed to load prompts.'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const visible = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (popularOnly && !r.is_popular) return false;
      if (!needle) return true;
      return (
        r.title.toLowerCase().includes(needle) ||
        r.prompt_text.toLowerCase().includes(needle) ||
        (r.description || '').toLowerCase().includes(needle)
      );
    });
  }, [rows, search, popularOnly]);

  const openNew = () => {
    setEditingId(null);
    setDraft({ ...BLANK_DRAFT, category_id: categoryFilter || categories[0]?.id || '' });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (p: WisdomPrompt) => {
    setEditingId(p.id);
    setDraft({
      category_id: p.category_id,
      title: p.title,
      prompt_text: p.prompt_text,
      description: p.description || '',
      is_popular: p.is_popular,
      is_active: p.is_active,
      sort_order: p.sort_order,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  /** Duplicate = open the create dialog pre-filled from an existing prompt. */
  const openDuplicate = (p: WisdomPrompt) => {
    setEditingId(null);
    setDraft({
      category_id: p.category_id,
      title: `${p.title} (copy)`,
      prompt_text: p.prompt_text,
      description: p.description || '',
      is_popular: false,
      is_active: p.is_active,
      sort_order: p.sort_order,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!draft.category_id) {
      setDialogError('Pick a wisdom pillar for this prompt.');
      return;
    }
    if (!draft.title.trim()) {
      setDialogError('Title is required.');
      return;
    }
    if (!draft.prompt_text.trim()) {
      setDialogError('Prompt text is required.');
      return;
    }
    setSaving(true);
    setDialogError(null);
    const payload: PromptInput = {
      category_id: draft.category_id,
      title: draft.title,
      prompt_text: draft.prompt_text,
      description: draft.description || null,
      is_popular: draft.is_popular,
      is_active: draft.is_active,
      sort_order: draft.sort_order,
    };
    try {
      if (editingId) {
        await lifeAdviceApi.prompts.update(editingId, payload);
        setToast('Prompt saved');
      } else {
        await lifeAdviceApi.prompts.create(payload);
        setToast('Prompt created');
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      setDialogError(writeErrorMessage(e, 'Could not save this prompt.'));
    } finally {
      setSaving(false);
    }
  };

  /** Star toggle straight from the grid — one PUT, optimistic snackbar. */
  const togglePopular = async (p: WisdomPrompt) => {
    try {
      await lifeAdviceApi.prompts.update(p.id, { is_popular: !p.is_popular });
      setRows((prev) =>
        prev.map((r) => (r.id === p.id ? { ...r, is_popular: !p.is_popular } : r))
      );
      setToast(!p.is_popular ? 'Marked as popular' : 'Removed from popular');
    } catch (e) {
      setToast(writeErrorMessage(e, 'Could not update this prompt.'));
    }
  };

  const removePrompt = async () => {
    if (!confirming) return;
    setConfirmBusy(true);
    try {
      const result = await lifeAdviceApi.prompts.remove(confirming.id);
      setToast(
        result.deactivated
          ? 'Prompt is used by member entries, so it was deactivated instead of deleted'
          : 'Prompt deleted'
      );
      setConfirming(null);
      reload();
    } catch (e) {
      setToast(writeErrorMessage(e, 'Could not delete this prompt.'));
      setConfirming(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const onCsvFile = (file: File | undefined) => {
    if (!file) return;
    setImportFileName(file.name);
    setImportError(null);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = () => setImportCsv(String(reader.result || ''));
    reader.onerror = () => setImportError('Could not read that file.');
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!importCsv.trim()) {
      setImportError('Choose a CSV file (or paste its contents) first.');
      return;
    }
    setImportBusy(true);
    setImportError(null);
    try {
      const result = await lifeAdviceApi.prompts.bulkImport(importCsv);
      setImportResult(result);
      if (result.inserted > 0) reload();
    } catch (e) {
      setImportError(writeErrorMessage(e, 'Bulk import failed.'));
    } finally {
      setImportBusy(false);
    }
  };

  const columns: GridColDef<WisdomPrompt>[] = [
    {
      field: 'is_popular',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<WisdomPrompt, boolean>) => (
        <IconButton
          size="small"
          aria-label={params.value ? `Unstar ${params.row.title}` : `Star ${params.row.title}`}
          onClick={(e) => {
            e.stopPropagation();
            togglePopular(params.row);
          }}
        >
          <StarIcon
            fontSize="small"
            sx={{ color: params.value ? brandColors.warmGold : 'action.disabled' }}
          />
        </IconButton>
      ),
    },
    {
      field: 'title',
      headerName: 'Prompt',
      flex: 1,
      minWidth: 280,
      renderCell: (params: GridRenderCellParams<WisdomPrompt, string>) => (
        <Tooltip title={params.row.prompt_text}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'category_name',
      headerName: 'Pillar',
      width: 190,
      valueGetter: (_value, row) => row.category_name || row.category_slug || '—',
    },
    { field: 'usage_count', headerName: 'Uses', width: 90, type: 'number' },
    { field: 'sort_order', headerName: 'Order', width: 90, type: 'number' },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams<WisdomPrompt, boolean>) =>
        params.value ? (
          <Chip size="small" label="Active" color="success" variant="outlined" />
        ) : (
          <Chip size="small" label="Hidden" variant="outlined" />
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<WisdomPrompt>) => (
        <Box>
          <IconButton
            size="small"
            aria-label={`Edit ${params.row.title}`}
            onClick={(e) => {
              e.stopPropagation();
              openEdit(params.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Duplicate ${params.row.title}`}
            onClick={(e) => {
              e.stopPropagation();
              openDuplicate(params.row);
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete ${params.row.title}`}
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(params.row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (down) {
    return (
      <EmptyState
        icon="error"
        title="LifeAdvice service is unreachable"
        description={`The admin API could not reach lifescribe-advice. ${error || ''}`}
        action={{ label: 'Retry', onClick: reload, variant: 'contained' }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<QuizIcon sx={{ fontSize: 32, color: brandColors.deepTeal }} />}
        title="Prompts"
        subtitle="The questions that start a member's advice. Starred prompts surface first in the app."
        helpId="lifeadvice-prompts"
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
              Refresh
            </Button>
            <Button
              startIcon={<UploadFileIcon />}
              onClick={() => {
                setImportOpen(true);
                setImportResult(null);
                setImportError(null);
              }}
            >
              Bulk import
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
              New prompt
            </Button>
          </Stack>
        }
      />

      {error && !down && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Pillar"
          size="small"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All pillars</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.icon} {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search prompts"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant={popularOnly ? 'contained' : 'outlined'}
          size="small"
          startIcon={<StarIcon />}
          onClick={() => setPopularOnly((v) => !v)}
        >
          {popularOnly ? 'Popular only' : 'All prompts'}
        </Button>
      </Stack>

      {!loading && visible.length === 0 ? (
        <EmptyState
          icon="empty_box"
          title={rows.length === 0 ? 'No prompts yet' : 'No prompts match these filters'}
          description={
            rows.length === 0
              ? 'Create a prompt, or bulk-import a CSV with category_slug, title and prompt_text columns.'
              : 'Try clearing the search box or the popular filter.'
          }
          action={
            rows.length === 0
              ? { label: 'New prompt', onClick: openNew, variant: 'contained' }
              : {
                  label: 'Clear filters',
                  onClick: () => {
                    setSearch('');
                    setPopularOnly(false);
                  },
                  variant: 'outlined',
                }
          }
        />
      ) : (
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={visible}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            disableRowSelectionOnClick
            onRowClick={(params) => openEdit(params.row as WisdomPrompt)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {/* ── Create / edit dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit prompt' : 'New prompt'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              select
              label="Wisdom pillar"
              value={draft.category_id}
              onChange={(e) => setDraft({ ...draft, category_id: e.target.value })}
              fullWidth
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.icon} {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label="Prompt text"
              value={draft.prompt_text}
              onChange={(e) => setDraft({ ...draft, prompt_text: e.target.value })}
              multiline
              minRows={3}
              fullWidth
              helperText="What the member reads before they start recording."
            />
            <TextField
              label="Coaching note"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              multiline
              minRows={2}
              fullWidth
              helperText="Optional nudge shown under the prompt."
            />
            <TextField
              label="Sort order"
              type="number"
              value={draft.sort_order}
              onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })}
              sx={{ maxWidth: 200 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.is_popular}
                  onChange={(e) => setDraft({ ...draft, is_popular: e.target.checked })}
                />
              }
              label="Popular (surfaced first)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                />
              }
              label="Offered to members"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save prompt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CSV bulk import ──────────────────────────────────────────────── */}
      <Dialog open={importOpen} onClose={() => !importBusy && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk import prompts</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The CSV needs a header row with at least <code>category_slug</code>, <code>title</code>{' '}
            and <code>prompt_text</code>. Optional columns: <code>description</code>,{' '}
            <code>is_popular</code>, <code>sort_order</code>. Up to 1,000 rows per import.
          </Typography>

          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ mb: 2 }}>
            Choose CSV file
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              aria-label="CSV file to import"
              onChange={(e) => onCsvFile(e.target.files?.[0])}
            />
          </Button>
          {importFileName && (
            <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
              {importFileName}
            </Typography>
          )}

          <TextField
            label="CSV contents"
            value={importCsv}
            onChange={(e) => setImportCsv(e.target.value)}
            multiline
            minRows={5}
            fullWidth
            placeholder={CSV_TEMPLATE}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
          />

          {importError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importError}
            </Alert>
          )}

          {importResult && (
            <Alert severity={importResult.errors.length > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
              <AlertTitle>
                {importResult.inserted} imported · {importResult.skipped} skipped
              </AlertTitle>
              {importResult.errors.slice(0, 10).map((err) => (
                <Typography key={err.line} variant="caption" sx={{ display: 'block' }}>
                  Line {err.line}: {err.error}
                </Typography>
              ))}
              {importResult.errors.length > 10 && (
                <Typography variant="caption">
                  …and {importResult.errors.length - 10} more.
                </Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setImportOpen(false)} variant="outlined" disabled={importBusy}>
            Close
          </Button>
          <Button onClick={runImport} variant="contained" disabled={importBusy}>
            {importBusy ? 'Importing…' : 'Import prompts'}
          </Button>
        </DialogActions>
      </Dialog>

      <LifeAdviceActionConfirm
        open={!!confirming}
        destructive
        loading={confirmBusy}
        title="Delete this prompt?"
        message={confirming ? `"${confirming.title}" will be removed from the app.` : ''}
        caption="If any member entry was written from this prompt the service deactivates it instead of deleting, so no member's advice is orphaned."
        confirmLabel="Delete prompt"
        pendingLabel="Deleting…"
        onConfirm={removePrompt}
        onCancel={() => setConfirming(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PromptsPage;
